import { lightenDarkenColor } from "utils/colorModification";
import ShapePattern from "./ShapePattern";
import StripePattern from "./StripePattern";
import { PredefinedPatternTypes } from "types/auth";

type GetPredefinedPatternOptions = {
  patternType: PredefinedPatternTypes;
  id: string;
  category: string;
  transparent: boolean;
  color: string;
};
export function createCustomCategoryPattern({
  patternType,
  id: featureId,
  category,
  transparent,
  color,
}: GetPredefinedPatternOptions) {
  const spaceOpacity = transparent ? 0 : 1;
  const outerFillOpacity = transparent ? 0 : 1;
  const clr = transparent ? "#7f7f7f" : lightenDarkenColor(color, -30);

  switch (patternType) {
    case "dot":
      return ShapePattern({
        id: featureId,
        type: category,
        transparent,
        width: 24,
        height: 24,
        backgroundFillColor: color,
        backgroundFillOpacity: outerFillOpacity,
        shape: "circle",
        shapeSize: 12,
        shapeFill: true,
        shapeFillColor: clr,
        orgColor: color,
      });

    case "dotDense":
      return ShapePattern({
        id: featureId,
        type: category,
        transparent,
        width: 16,
        height: 16,
        backgroundFillColor: color,
        backgroundFillOpacity: outerFillOpacity,
        shape: "circle",
        shapeSize: 8,
        shapeFill: true,
        shapeFillColor: clr,
        orgColor: color,
      });

    case "dotSparse":
      return ShapePattern({
        id: featureId,
        type: category,
        transparent,
        width: 30,
        height: 30,
        backgroundFillColor: color,
        backgroundFillOpacity: outerFillOpacity,
        shape: "circle",
        shapeSize: 12,
        shapeFill: true,
        shapeFillColor: clr,
        orgColor: color,
      });

    case "ring":
      return ShapePattern({
        id: featureId,
        type: category,
        transparent,
        width: 24,
        height: 24,
        backgroundFillColor: color,
        backgroundFillOpacity: outerFillOpacity,
        shape: "ring",
        shapeSize: 12,
        shapeFill: false,
        shapeStroke: true,
        shapeColor: clr,
        shapeWeight: 2,
        orgColor: color,
      });

    case "diamond":
      return ShapePattern({
        id: featureId,
        type: category,
        transparent,
        width: 24,
        height: 24,
        backgroundFillColor: color,
        backgroundFillOpacity: outerFillOpacity,
        shape: "diamond",
        shapeSize: 10,
        shapeFill: true,
        shapeFillColor: clr,
        orgColor: color,
      });

    case "square":
      return ShapePattern({
        id: featureId,
        type: category,
        transparent,
        width: 24,
        height: 24,
        backgroundFillColor: color,
        backgroundFillOpacity: outerFillOpacity,
        shape: "square",
        shapeSize: 10,
        shapeFill: true,
        shapeFillColor: clr,
        orgColor: color,
      });

    case "triangle":
      return ShapePattern({
        id: featureId,
        type: category,
        transparent,
        width: 24,
        height: 24,
        backgroundFillColor: color,
        backgroundFillOpacity: outerFillOpacity,
        shape: "triangle",
        shapeSize: 10,
        shapeFill: true,
        shapeFillColor: clr,
        orgColor: color,
      });

    case "cross":
      return ShapePattern({
        id: featureId,
        type: category,
        transparent,
        width: 24,
        height: 24,
        backgroundFillColor: color,
        backgroundFillOpacity: outerFillOpacity,
        shape: "cross",
        shapeSize: 10,
        shapeFill: true,
        shapeFillColor: clr,
        orgColor: color,
      });

    case "x":
      return ShapePattern({
        id: featureId,
        type: category,
        transparent,
        width: 24,
        height: 24,
        backgroundFillColor: color,
        backgroundFillOpacity: outerFillOpacity,
        shape: "x",
        shapeSize: 10,
        shapeFill: true,
        shapeFillColor: clr,
        orgColor: color,
      });

    case "diagonalStripe":
      return StripePattern({
        id: featureId,
        type: category,
        transparent,
        angle: 45,
        width: 20,
        height: 20,
        weight: 2,
        color: clr,
        opacity: 1,
        backgroundFillColor: color,
        backgroundFillOpacity: spaceOpacity,
        orgColor: color,
      });

    case "diagonalStripeDashed":
      return StripePattern({
        id: featureId,
        type: category,
        transparent,
        angle: 45,
        width: 20,
        height: 20,
        weight: 2,
        color: clr,
        opacity: 1,
        dashArray: "2,5",
        backgroundFillColor: color,
        backgroundFillOpacity: spaceOpacity,
        orgColor: color,
      });

    case "diagonalStripeDense":
      return StripePattern({
        id: featureId,
        type: category,
        transparent,
        angle: 45,
        width: 10,
        height: 10,
        weight: 2,
        color: clr,
        opacity: 1,
        backgroundFillColor: color,
        backgroundFillOpacity: spaceOpacity,
        stripeCount: 1,
        orgColor: color,
      });

    case "diagonalStripeDashedDense":
      return StripePattern({
        id: featureId,
        type: category,
        transparent,
        angle: 45,
        width: 10,
        height: 10,
        weight: 2,
        color: clr,
        opacity: 1,
        dashArray: "2,5",
        backgroundFillColor: color,
        backgroundFillOpacity: spaceOpacity,
        stripeCount: 1,
        orgColor: color,
      });

    case "horizontalStripe":
      return StripePattern({
        id: featureId,
        type: category,
        transparent,
        angle: 0,
        width: 20,
        height: 20,
        weight: 2,
        color: clr,
        opacity: 1,
        backgroundFillColor: color,
        backgroundFillOpacity: spaceOpacity,
        orgColor: color,
      });

    case "horizontalStripeDashed":
      return StripePattern({
        id: featureId,
        type: category,
        transparent,
        angle: 0,
        width: 20,
        height: 20,
        weight: 2,
        color: clr,
        opacity: 1,
        dashArray: "2,5",
        backgroundFillColor: color,
        backgroundFillOpacity: spaceOpacity,
        orgColor: color,
      });

    case "horizontalStripeDense":
      return StripePattern({
        id: featureId,
        type: category,
        transparent,
        angle: 0,
        width: 10,
        height: 10,
        weight: 2,
        color: clr,
        opacity: 1,
        backgroundFillColor: color,
        backgroundFillOpacity: spaceOpacity,
        orgColor: color,
      });

    case "horizontalStripeDashedDense":
      return StripePattern({
        id: featureId,
        type: category,
        transparent,
        angle: 0,
        width: 10,
        height: 10,
        weight: 2,
        color: clr,
        opacity: 1,
        dashArray: "2,5",
        backgroundFillColor: color,
        backgroundFillOpacity: spaceOpacity,
        orgColor: color,
      });

    case "verticalStripe":
      return StripePattern({
        id: featureId,
        type: category,
        transparent,
        angle: 90,
        width: 20,
        height: 20,
        weight: 2,
        color: clr,
        opacity: 1,
        backgroundFillColor: color,
        backgroundFillOpacity: spaceOpacity,
        orgColor: color,
      });

    case "verticalStripeDashed":
      return StripePattern({
        id: featureId,
        type: category,
        transparent,
        angle: 90,
        width: 20,
        height: 20,
        weight: 2,
        color: clr,
        opacity: 1,
        dashArray: "2,5",
        backgroundFillColor: color,
        backgroundFillOpacity: spaceOpacity,
        orgColor: color,
      });

    case "verticalStripeDense":
      return StripePattern({
        id: featureId,
        type: category,
        transparent,
        angle: 90,
        width: 10,
        height: 10,
        weight: 2,
        color: clr,
        opacity: 1,
        backgroundFillColor: color,
        backgroundFillOpacity: spaceOpacity,
        orgColor: color,
      });

    case "verticalStripeDashedDense":
      return StripePattern({
        id: featureId,
        type: category,
        transparent,
        angle: 90,
        width: 10,
        height: 10,
        weight: 2,
        color: clr,
        opacity: 1,
        dashArray: "2,5",
        backgroundFillColor: color,
        backgroundFillOpacity: spaceOpacity,
        orgColor: color,
      });

    // case "grid":
    //   return GridPattern({
    //     id: featureId,
    //     type: category,
    //     transparent,
    //     width: 24,
    //     height: 24,
    //     spacing: 12,
    //     backgroundFillColor: color,
    //     backgroundFillOpacity: outerFillOpacity,
    //     lineColor: clr,
    //     lineWeight: 1,
    //   });

    default:
      return null;
  }
}
